import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Button, message } from 'antd';
import axios from 'axios';
import { useQuery } from 'react-query';
import { LeftOutlined } from '@ant-design/icons';
import CollectionsListHeader from './components/CollectionsListHeader';
import CollectionsList from './components/CollectionsList';
import CollectionProductsListHeader from './components/CollectionProductsListHeader';
import CollectionProductsList from './components/CollectionProductsList';
import CollectionProductHeader from './components/CollectionProductHeader';
import CollectionProduct from './components/CollectionProduct';
import './styles.css';
import NameCollectionModal from './components/NameCollectionModal';
import {
  addProductToClientCollection,
  deleteCollection,
  deleteProductFromCollection,
  deleteVendorItemFromCollection,
  deleteAttachmentFromCollection,
} from '../../../../../api/collections';
import { API_URL } from '../../../../../constants';
import { postRequest } from '../../../../../core/apiRequests';
import PopConfirm from '../../../../Layout/PopConfirm';
import ShareCollectionModal from './components/ShareCollectionModal';
import UploadAttachmentModal from './components/UploadAttachmentModal';
import useSearchParams from '../../../../../hooks/useSearchParams';
import { addVendorItemToClientCollection, getVendors } from '../../../../../api/vendors';
import { DragAndDropContextProvider } from '../../../../../context/DragDropContext';

const Collections = ({ ...props }) => {
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [type, setType] = useState('product-all');

  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState([]);

  const [collectionProducts, setCollectionProducts] = useState([]);

  const { setValue, getValue: getSearchParam } = useSearchParams();
  const selectedCollectionId = getSearchParam('collectionId');
  const clientId = getSearchParam('client') || props.client?.client_id;

  const params = useMemo(() => new URLSearchParams(window.location.search), []);


  const setSelectedCollectionId = useCallback(collection => {
    setValue('collectionId', collection?.id);
  }, []);

  const fetchCollectionProducts = useCallback(async () => {
    setCollectionProducts([]);
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/xa/clients/${clientId}/collections-v2/${selectedCollectionId}/products`,
      );
      const data = response.data;
      setCollectionProducts([
        ...(data.collectionProducts?.map(item => ({ ...item.product, type: 'product' })) ?? []),
        ...(data.collectionVendorItems?.map(item => ({ ...item.item, type: 'vendor' })) ?? []),
        ...(data?.collectionAttachments?.map(item => ({ ...item, type: 'attachment' })) ?? []),
      ]);
    } catch (error) {
      message.error('Error Fetching Collection Products');
    } finally {
      setLoading(false);
    }
  }, [selectedCollectionId, collections]);

  useEffect(() => {
    if (selectedCollectionId) {
      const collection = collections.find(item => item.id == selectedCollectionId);
      setSelectedCollection(collection);
      setSelectedProduct(null);
      setShowAddProduct(false);
      fetchCollectionProducts();
    } else {
      setSelectedCollection(null);
      setSelectedProduct(null);
      setShowAddProduct(false);
    }
  }, [selectedCollectionId, collections]);

  const toggleShowAddProduct = useCallback(() => {
    setShowAddProduct(!showAddProduct);
  }, [showAddProduct]);

  const fetchCollections = useCallback(async () => {
    if (props.client.client_id) {
      setLoading(true);

      try {
        const response = await axios.get(`${API_URL}/xa/clients/${props.client.client_id}/collections-v2`);
        setCollections(response.data);
      } catch (error) {
        message.error('Error Fetching Collections');
      } finally {
        setLoading(false);
      }
    }
  }, [props.client.client_id]);

  useEffect(() => {
    fetchCollections();
  }, [props.client.client_id]);

  const onAddToCollection = async () => {
    if (selectedProducts.size > 0) {
      const productIds = [...selectedProducts];
      if (type.includes('brands')) {
        const requestData = {
          vendorItemIds: productIds,
        };

        const res = await addVendorItemToClientCollection(
          props.client?.client_id,
          selectedCollection?.id,
          requestData,
        );
        if (res) {
          message.success(`Products added to "${selectedCollection.name}"`);
          fetchCollections();
        }
      } else {
        const res = await addProductToClientCollection({
          clientId: props.client?.client_id,
          collectionId: selectedCollection?.id,
          data: {
            productId: productIds,
          },
        });
        if (res) {
          message.success(`Products added to "${selectedCollection.name}"`);
          fetchCollections();
        }
      }
    }
    setSelectedProducts(new Set());
    toggleShowAddProduct();
  };

  const handleDeleteProductFromCollection = useCallback(
    async id => {
      const res = await deleteProductFromCollection({
        clientId: props.client?.client_id,
        collectionId: selectedCollection?.id,
        productId: id,
      });

      if (res) {
        message.success(`Product removed from "${selectedCollection.name}"`);
        fetchCollections();
      }
    },
    [props.client, selectedCollection],
  );

  const handleDeleteVendorItemFromCollection = useCallback(
    async vendorItemId => {
      const res = await deleteVendorItemFromCollection({
        clientId: props.client?.client_id,
        collectionId: selectedCollection?.id,
        vendorItemId,
      });

      if (res) {
        message.success(`Product removed from "${selectedCollection.name}"`);
        fetchCollections();
      }
    },
    [props.client, selectedCollection],
  );

  const handleDeleteAttachmentFromCollection = useCallback(
    async attachmentId => {
      const res = await deleteAttachmentFromCollection({
        collectionId: selectedCollection?.id,
        data: { attachmentId: `${attachmentId}` },
      });

      if (res) {
        message.success(`Attachment removed from "${selectedCollection.name}"`);
        fetchCollections();
      }
    },
    [props.client, selectedCollection],
  );

  const onDeleteToCollection = async (id, type = 'product') => {
    if (type === 'vendor') {
      handleDeleteVendorItemFromCollection(id);
    } else if (type === 'attachment') {
      handleDeleteAttachmentFromCollection(id);
    } else {
      handleDeleteProductFromCollection(id);
    }
  };

  const toggleProductSelection = useCallback(
    productId => {
      setSelectedProducts(oldSet => {
        const newSet = new Set(oldSet);
        if (newSet.has(productId)) newSet.delete(productId);
        else newSet.add(productId);
        return newSet;
      });
    },
    [setSelectedProducts],
  );

  const handleGoBack = useCallback(() => {
    if (showAddProduct) {
      setSelectedProducts(new Set());
      toggleShowAddProduct();
    } else {
      setSelectedCollectionId(null);
    }
  }, [showAddProduct]);

  const handleNameCollection = name => {
    if (props.client.client_id) {
      if (selectedCollection?.id) {
        const reqObj = {
          params: [
            'clients',
            props.client.client_id,
            'collections',
            selectedCollection.id,
            'rename-collection',
          ],
          data: {
            name,
          },
        };
        (async () => {
          const resp = await postRequest(reqObj);
          if (resp) {
            const findIndex = collections.findIndex(item => item.id == selectedCollection.id);
            const updatesCollections = [...collections];
            updatesCollections[findIndex].name = name;
            setCollections(updatesCollections);
            message.success('Collection renamed!');
            setShowRenameModal(false);
          } else {
            message.error('Failed To create collection');
            setShowRenameModal(false);
          }
        })();
      } else {
        const reqObj = {
          params: ['clients', props.client.client_id, 'collections'],
          data: {
            name,
          },
        };
        (async () => {
          const { collectionId } = await postRequest(reqObj);
          if (collectionId) {
            message.success('Collection Created!');
            const newCollection = {
              clientId: props.client.client_id,
              id: collectionId,
              isShared: false,
              name: name,
              collectionProducts: [],
              collectionVendorItems: [],
              collectionAttachments: [],
            };
            setSelectedCollectionId(newCollection);
            setCollections([...collections, newCollection]);
            setShowCreateModal(false);
          } else {
            message.error('Failed To create collection');
            setShowCreateModal(false);
          }
        })();
      }
    }
  };

  const deleteCustomerCollection = () => {
    (async () => {
      const respObj = await deleteCollection({
        clientId: props.client?.client_id,
        collectionId: selectedCollection?.id,
      });
      if (respObj) {
        setShowDeleteModal(false);
        message.success('Collection deleted!');
        const filteredCollections = collections.filter(c => c.id !== selectedCollection?.id);
        setCollections(filteredCollections);
        setSelectedCollectionId(null);
      } else {
        message.error('Error deleting collection');
      }
    })();
  };

  const changeShareCollectionState = useCallback(() => {
    const findIndex = collections.findIndex(item => item.id == selectedCollection.id);
    const updatesCollections = [...collections];
    updatesCollections[findIndex].collection_is_shared = true;
    setCollections(updatesCollections);
  }, [selectedCollection, collections]);

  const { data: vendors, isFetching: isFetchingVendors } = useQuery(
    'getVendors',
    async () =>
      getVendors({
        params: {
          offset: 0,
          limit: 100,
          enabled: true,
        },
      }),
    {
      refetchOnWindowFocus: false,
      select: response => response?.data?.data,
      initialData: [],
    },
  );

  const handleOpenProductPage = useCallback(type => {
    setType(type);
    toggleShowAddProduct();
  }, []);

  const openAttachmentUploadModal = useCallback(() => {
    setShowUploadModal(true);
  }, []);

  if (selectedProduct) {
    return (
      <div className="clients-v2-collections--container">
        <Button
          type="text"
          size="large"
          icon={<LeftOutlined />}
          onClick={() => setSelectedProduct(null)}
          className="clients-v2-collections--back"
        />

        <div className="clients-v2-collections">
          <CollectionProductHeader
            selectedProduct={selectedProduct}
            onDeleteToCollection={onDeleteToCollection}
            selectedCollection={selectedCollection}
            setSelectedProduct={setSelectedProduct}
          />
          <CollectionProduct selectedProduct={selectedProduct} />
        </div>
      </div>
    );
  }

  if (selectedCollection) {
    return (
      <div className="clients-v2-collections--container">
        <Button
          type="text"
          size="large"
          icon={<LeftOutlined />}
          onClick={handleGoBack}
          className="clients-v2-collections--back"
        />

        <div className="clients-v2-collections">
          <CollectionProductsListHeader
            selectedProducts={selectedProducts}
            selectedCollection={selectedCollection}
            setSelectedCollection={setSelectedCollectionId}
            showAddProduct={showAddProduct}
            saveButtonProps={{
              disabled: selectedProducts.size === 0,
              onClick: onAddToCollection,
            }}
            openAttachmentUploadModal={openAttachmentUploadModal}
            toggleShowAddProduct={toggleShowAddProduct}
            clientId={props.client?.client_id}
            openRenameModal={() => setShowRenameModal(true)}
            openDeleteModal={() => setShowDeleteModal(true)}
            openShareModal={() => setShowShareModal(true)}
            handleOpenProductPage={handleOpenProductPage}
            vendors={vendors}
            isFetchingVendors={isFetchingVendors}
          />
          <CollectionProductsList
            collection={selectedCollection}
            setSelectedProduct={setSelectedProduct}
            listItemsStyles={{ height: '100%' }}
            toggleShowAddProduct={toggleShowAddProduct}
            showAddProduct={showAddProduct}
            toggleProductSelection={toggleProductSelection}
            selectedProducts={selectedProducts}
            handleOpenProductPage={handleOpenProductPage}
            openAttachmentUploadModal={openAttachmentUploadModal}
            vendors={vendors}
            isFetchingVendors={isFetchingVendors}
            type={type}
            loading={loading}
            collectionProducts={collectionProducts}
            fetchCollectionProducts={fetchCollectionProducts}
          />
          {showRenameModal && (
            <NameCollectionModal
              onClose={() => setShowRenameModal(false)}
              setSelectedCollection={setSelectedCollectionId}
              onSaveCollection={handleNameCollection}
              collection={selectedCollection}
            />
          )}
          {showDeleteModal && (
            <PopConfirm
              confirm={`Are you sure you want to delete "${selectedCollection.name}" ?`}
              description="This will permanetly delete this collection and shared links will no longer be valid"
              onCancel={() => setShowDeleteModal(false)}
              onConfirm={deleteCustomerCollection}
            />
          )}
          {showShareModal && (
            <ShareCollectionModal
              client={props.client}
              collection={selectedCollection}
              onClose={() => setShowShareModal(false)}
              auth={props.auth}
              changeShareCollectionState={changeShareCollectionState}
            />
          )}
          {showUploadModal && (
            <DragAndDropContextProvider>
              <UploadAttachmentModal
                onClose={() => setShowUploadModal(false)}
                setSelectedCollection={setSelectedCollection}
                fetchCollectionProducts={fetchCollectionProducts}
                collection={selectedCollection}
              />
            </DragAndDropContextProvider>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="clients-v2-collections">
      <CollectionsListHeader
        setShowCreateModal={setShowCreateModal}
        showCreateModal={showCreateModal}
        flags={props?.auth?.merchantFlags}
      />
      <CollectionsList
        setSelectedCollection={setSelectedCollectionId}
        collections={collections}
        loading={loading}
      />
      {showCreateModal && (
        <NameCollectionModal
          onClose={() => setShowCreateModal(false)}
          client={props.client}
          setSelectedCollection={setSelectedCollectionId}
          onSaveCollection={handleNameCollection}
        />
      )}
    </div>
  );
};

export default Collections;
